import React, { useMemo } from 'react';
import { TemplateType } from '@server/trading-templates/schemas/input.schema';
import {
  GridActionConfig,
  ManageGrid
} from '@client/foundation/shared/components/manage-grid';
import { useTemplateGridRows } from './use-template-grid-rows.hook';
import { useTemplateGridColumns } from './use-template-grid-columns.hook';
import { canUseDefaultTemplate } from './template-grid.utils';
import { TemplateRow } from './template-grid.types';

export type TemplateGridProps = {
  templateType: TemplateType;
  onApply?: (templateIds: string[]) => void;
  onAdd?: (templateIds: string[]) => void;
  onDelete: (templateIds: string[]) => void;
  onSetAsDefault: (templateId: string, isDefault: boolean) => void;
  onModify?: (templateId: string, templateName: string) => void;
  onDuplicate?: (templateId: string, templateName: string) => void;
  onShare?: (templateId: string, templateName: string) => void;
};

export const TemplateGrid: React.FC<TemplateGridProps> = ({
  templateType,
  onApply,
  onAdd,
  onDelete,
  onSetAsDefault,
  onModify,
  onDuplicate,
  onShare
}) => {
  const allowDefaultToggle = canUseDefaultTemplate(templateType);
  const canAddTemplate =
    templateType === 'indicators' ||
    templateType === 'strategy' ||
    templateType === 'signalBlock';
  const { rows } = useTemplateGridRows({ templateType, allowDefaultToggle });
  const columns = useTemplateGridColumns({ templateType });
  const actions = useMemo<GridActionConfig<TemplateRow>[]>(() => {
    const next: GridActionConfig<TemplateRow>[] = [
      {
        key: 'clear',
        label: 'Clear',
        tone: 'secondary',
        selectionMode: 'multi',
        onClick: ({ clearSelection }) => clearSelection()
      },
      {
        key: 'delete',
        label: 'Delete',
        tone: 'danger',
        selectionMode: 'multi',
        onClick: ({ selectedIds, clearSelection }) => {
          onDelete(selectedIds);
          clearSelection();
        }
      }
    ];

    if (allowDefaultToggle) {
      next.push({
        key: 'toggle-default',
        label: 'Toggle default',
        selectionMode: 'single',
        onClick: ({ selectedRow }) => {
          if (!selectedRow) return;
          onSetAsDefault(selectedRow.id, selectedRow.isDefault);
        }
      });
    }

    if (onModify) {
      next.push({
        key: 'modify',
        label: 'Modify',
        selectionMode: 'single',
        onClick: ({ selectedRow }) => {
          if (!selectedRow) return;
          onModify(selectedRow.id, selectedRow.name);
        }
      });
    }

    if (onDuplicate) {
      next.push({
        key: 'duplicate',
        label: 'Duplicate',
        selectionMode: 'single',
        onClick: ({ selectedRow }) => {
          if (!selectedRow) return;
          onDuplicate(selectedRow.id, selectedRow.name);
        }
      });
    }

    if (onShare) {
      next.push({
        key: 'share',
        label: 'Share',
        selectionMode: 'single',
        onClick: ({ selectedRow }) => {
          if (!selectedRow) return;
          onShare(selectedRow.id, selectedRow.name);
        }
      });
    }

    if (canAddTemplate && onAdd) {
      next.push({
        key: 'add',
        label: 'Add',
        selectionMode: 'single',
        onClick: ({ selectedRows }) => onAdd(selectedRows.map((row) => row.id))
      });
    }

    if (onApply) {
      next.push({
        key: 'apply',
        label: canAddTemplate && onAdd ? 'Replace' : 'Apply',
        selectionMode: 'single',
        onClick: ({ selectedRows }) =>
          onApply(selectedRows.map((row) => row.id))
      });
    }

    return next;
  }, [
    allowDefaultToggle,
    canAddTemplate,
    onAdd,
    onApply,
    onDelete,
    onDuplicate,
    onModify,
    onSetAsDefault,
    onShare
  ]);

  return (
    <ManageGrid
      rows={rows}
      columns={columns}
      actions={actions}
    />
  );
};
